import React, { useEffect, useState } from 'react';
import {
  ArrayField,
  BooleanField,
  BooleanInput,
  ChipField,
  Create,
  Edit,
  EditButton,
  Filter,
  FormDataConsumer,
  maxLength,
  maxValue,
  minLength,
  minValue,
  number,
  NumberInput,
  ReferenceArrayInput,
  ReferenceField,
  useTranslate,
  required,
  SingleFieldList,
  TextField,
  TextInput,
} from 'react-admin';
import dataProvider from '../../dataProvider';
import withStyles from '@material-ui/core/styles/withStyles';
import LanguageIcon from '@material-ui/icons/Language';
import { Link } from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import { ZippityDatagrid, ZippitySimpleForm } from '../../components';
import EmployeeChipsField from '../employee/EmployeeChipsField';
import ScheduleTypeChipsField from '../schedule_type/ScheduleTypeChipsField';
import BulkDeleteButton from '../../components/BulkDeleteButton';
import CustomTitle from '../../components/CustomTitle';
import ReferenceArrayChild from '../../components/ReferenceArrayChild';
import {
  formatScheduleTypeName,
  perPage,
  getZ3PConfigValue,
} from '../../helper';
import { printFullAddress } from '../customer-address';
import SearchLocationInput from '../customer/SearchLocationInput';
import AddressFields from '../client_locations/AddressFields';
import {
  addressValidation,
  SaveToolbarWithAddressCreation,
} from '../client_locations';
import ListWithCSVImport from '../../components/ListWithCSVImport';
import Tooltip from '@material-ui/core/Tooltip';

const ServiceVehicleFilter = (props) => (
  <Filter {...props}>
    <BooleanInput source="is_active" defaultValue />
  </Filter>
);

const listStyles = {
  nb_commands: { color: 'purple' },
  center: { textAlign: 'center' },
};

export const ServiceVehicleList = withStyles(listStyles)(
  ({ classes, ...props }) => {
    const [vehicleCount, setVehicleCount] = useState(0);
    const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');
    useEffect(() => {
      dataProvider
        .getList('service-vehicle', {
          filter: {
            is_active: true,
          },
          sort: { field: 'name', order: 'ASC' },
          pagination: { page: 1, perPage: 2000 },
        })
        .then(({ data }) => {
          setVehicleCount(data.length);
          localStorage.setItem('vehicleCount', data.length);

        })
        .catch((error) => {
          console.log(error);
        });
    }, []);

    return (
      <div>
        <ListWithCSVImport
          {...props}
          filter={{ is_service_bay: false }}
          filters={<ServiceVehicleFilter />}
          bulkActionButtons={<BulkDeleteButton />}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={50}
        >
          <ZippityDatagrid>
            <TextField source="created_at" />
            <TextField source="modified_at" />
            <TextField source="service_vehicle_id" />
            <TextField source="name" />
            <TextField source="aap_customer_name" />
            <TextField source="aap_customer_id" />
            <TextField source="slack_channel_name" />
            <TextField source="quickbooks_class_id" />
            <EmployeeChipsField source="employees" />
            {!isSkippingVehicle ? (
              <ScheduleTypeChipsField
                source="default_schedule_types"
                sortable={false}
              />
            ) : (
              <></>
            )}
            <TextField source="shorter_transit_radius_minutes" />
            <TextField source="default_buffer_time_minutes" />
            <ReferenceField
              source="address_id"
              reference="customer-address"
              sortable={false}
              allowEmpty
            >
              <TextField source="one_line_human_readable_address" />
            </ReferenceField>
            <BooleanField source="is_active" />
            <ArrayField source="service_areas">
              <SingleFieldList linkType={false}>
                <ChipField size="small" source="postal_code" />
              </SingleFieldList>
            </ArrayField>
            <EditButton />
          </ZippityDatagrid>
        </ListWithCSVImport>
      </div>
    );
  },
);

const editStyles = {
  service_vehicle_id: { display: 'inline-block' },
  referenceArrayWide: { width: '100%' },
  marginBottom: { marginBottom: 20 },
  serviceAreaLink: {
    fontSize: 14,
    color: 'rgba(0, 0, 0, 0.87)',
    display: 'flex',
    alignItems: 'center',
  },
  serviceAreaGlobe: {
    fontSize: 20,
    margin: '8px',
  },
  serviceAreaArrow: {
    fontSize: 14,
    margin: '4px',
  },
  addressSection: {
    display: 'flex',
    alignItems: 'first baseline',
  },
  addressElement: {
    margin: 10,
  },
};

const validateName = [required(), minLength(2), maxLength(100)];
const validateNumber = [required(), minLength(2), maxLength(100), number()];
const validateString = [minLength(2), maxLength(100)];
const validateMinutes = [minValue(10), maxValue(9999)];

export const ServiceVehicleEdit = withStyles(editStyles)(
  ({ classes, ...props }) => {
    const translate = useTranslate();
    const [showNewAddress, setShowNewAddress] = useState(false);
    const [address, setAddress] = useState(null);
    const [vehicleCount, setVehicleCount] = useState(0);
    const [addressErrors, setAddressErrors] = useState([]);

    useEffect(() => {
      dataProvider
        .getList('service-vehicle', {
          filter: {
            is_active: true,
          },
          sort: { field: 'name', order: 'ASC' },
          pagination: { page: 1, perPage: 2000 },
        })
        .then(({ data }) => {
          setVehicleCount(data.length);
        })
        .catch((error) => {
          console.log(error);
        });
    }, []);
    

    const setAddressWithValidation = (addressValues) => {
      addressValidation(addressValues, addressErrors, setAddressErrors);
      setAddress(addressValues);
    };

    const handleChangeField = (event) => {
      const fieldObject = { [event.target.id]: event.target.value };
      const newAddress = {
        ...address,
        ...fieldObject,
      };
      setAddressWithValidation(newAddress);
    };

    const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');
    return (
      <Edit
        undoable={false}
        title={<CustomTitle title="resources.service-vehicle.title" />}
        {...props}
      >
        <ZippitySimpleForm
          toolbar={
            <SaveToolbarWithAddressCreation
              address={address}
              addressHasError={addressErrors.length > 0}
            />
          }
        >
          <TextField
            source="service_vehicle_id"
            formClassName={classes.service_vehicle_id}
          />
          <TextInput validate={validateName} source="name" />
          <TextInput validate={validateName} source="aap_customer_name" />
          <TextInput validate={validateNumber} source="aap_customer_id" />
          <TextInput validate={validateString} source="slack_channel_name" />
          <TextInput validate={validateString} source="quickbooks_class_id" />
          {!isSkippingVehicle ? (
            <ReferenceArrayInput
              source="default_schedule_type_ids"
              reference="schedule-type"
              perPage={100}
              className={classes.referenceArrayWide}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <ReferenceArrayChild
                {...props}
                optionText={(option) =>
                  formatScheduleTypeName(option.name, translate)
                }
                source="default_schedule_type_ids"
              />
            </ReferenceArrayInput>
          ) : (
            <></>
          )}
          <ReferenceArrayInput
            source="employee_ids"
            reference="employee"
            perPage={perPage}
            className={classes.referenceArrayWide}
            sort={{ field: 'email_address', order: 'ASC' }}
          >
            <ReferenceArrayChild
              {...props}
              optionText="email_address"
              source="employee_ids"
            />
          </ReferenceArrayInput>
          <NumberInput
            source="shorter_transit_radius_minutes"
            validate={validateMinutes}
            fullWidth
          />
          <NumberInput
            source="default_buffer_time_minutes"
            fullWidth
            helperText="Add additional time to each appointment to account for flexibility, setting up, traffic and more"
          />
          <FormDataConsumer source="address_id">
            {({ formData: data, ...props }) => {
              if (!showNewAddress && !!data.address_id) {
                return (
                  <div className={classes.addressSection}>
                    <ReferenceField
                      source="address_id"
                      reference="customer-address"
                      link={false}
                      {...props}
                    >
                      <FormDataConsumer>
                        {({ record }) => {
                          return <p>{printFullAddress(record)}</p>;
                        }}
                      </FormDataConsumer>
                    </ReferenceField>
                    <Link
                      onClick={() => setShowNewAddress(true)}
                      className={classes.addressElement}
                    >
                      Edit Address
                    </Link>
                  </div>
                );
              }
              return (
                <>
                  <div>Enter Address:</div>
                  <div style={{ width: '50%' }}>
                    <SearchLocationInput
                      setAddress={setAddressWithValidation}
                    />
                  </div>
                  <AddressFields
                    address={address}
                    handleChangeField={handleChangeField}
                    addressErrors={addressErrors}
                  />
                </>
              );
            }}
          </FormDataConsumer>

          <Tooltip title= {vehicleCount>1 ? "" : "At least 1 vehicle should be in a active status to receive appointments" } arrow placement="right-start">
          <span data-tip="" data-tip-disable={false}>
          <BooleanInput source="is_active" disabled={vehicleCount>1 ? false : true }/>
          </span>
        </Tooltip>

         
          <FormDataConsumer source="service-area">
            {({ formData }) => (
              <Link
                href={`#/service-area/${formData.id}`}
                color="primary"
                className={classes.serviceAreaLink}
              >
                <LanguageIcon className={classes.serviceAreaGlobe} />
                Zip Code Service Area{' '}
                <LaunchIcon className={classes.serviceAreaArrow} />
              </Link>
            )}
          </FormDataConsumer>
        </ZippitySimpleForm>
      </Edit>
    );
  },
);

export const ServiceVehicleCreate = withStyles(editStyles)(
  ({ classes, ...props }) => {
    const translate = useTranslate();
    const [address, setAddress] = useState(null);
    const [addressErrors, setAddressErrors] = useState([]);

    const setAddressWithValidation = (addressValues) => {
      addressValidation(addressValues, addressErrors, setAddressErrors);
      setAddress(addressValues);
    };

    const handleChangeField = (event) => {
      const fieldObject = { [event.target.id]: event.target.value };
      const newAddress = {
        ...address,
        ...fieldObject,
      };
      setAddressWithValidation(newAddress);
    };

    const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');

    return (
      <Create {...props}>
        <ZippitySimpleForm
          toolbar={
            <SaveToolbarWithAddressCreation
              address={address}
              addressIsRequired
              addressHasError={addressErrors.length > 0}
            />
          }
        >
          <TextInput validate={validateName} source="name" />
          <TextInput validate={validateName} source="aap_customer_name" />
          <TextInput validate={validateNumber} source="aap_customer_id" />
          <TextInput validate={validateString} source="slack_channel_name" />
          <TextInput validate={validateString} source="quickbooks_class_id" />
          {!isSkippingVehicle ? (
            <ReferenceArrayInput
              source="default_schedule_type_ids"
              reference="schedule-type"
              perPage={100}
              className={classes.referenceArrayWide}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <ReferenceArrayChild
                {...props}
                optionText={(option) =>
                  formatScheduleTypeName(option.name, translate)
                }
                source="default_schedule_type_ids"
              />
            </ReferenceArrayInput>
          ) : (
            <></>
          )}
          <ReferenceArrayInput
            source="employee_ids"
            reference="employee"
            perPage={perPage}
            className={classes.referenceArrayWide}
            sort={{ field: 'email_address', order: 'ASC' }}
          >
            <ReferenceArrayChild
              {...props}
              optionText="email_address"
              source="employee_ids"
            />
          </ReferenceArrayInput>
          <NumberInput
            validate={validateMinutes}
            source="shorter_transit_radius_minutes"
            defaultValue={60}
            fullWidth
          />
          <NumberInput
            source="default_buffer_time_minutes"
            fullWidth
            helperText="Add additional time to each appointment to account for flexibility, setting up, traffic and more"
          />
          <div>Address:</div>
          <SearchLocationInput
            setAddress={setAddressWithValidation}
            isRequired
          />
          <AddressFields
            address={address}
            handleChangeField={handleChangeField}
            addressErrors={addressErrors}
          />
          <BooleanInput source="is_active" defaultValue />
        </ZippitySimpleForm>
      </Create>
    );
  },
);
